import React from "react";
import {
  BUSY_RIDER_STATES,
  IDLE_RIDER_STATES,
  PAKISTAN_LAT_LONG
} from "../../utils/constants";
import { RiderInfoWindow } from "./rider-info-window";
import { OrderInfoWindow } from "./order-info-window";
import { CollectionCenterInfoWindow } from "./collection-center-info-window";
import { CellInfoWindow } from "./cell-info-window";
import Map from "../commons/Map";
import { Marker, Popup, Polygon } from "react-leaflet";
import {
  BusyRiderIcon,
  CellIcon,
  CollectionCenterIcon,
  CovidRiderIcon,
  CustomerIcon,
  IdleRiderIcon,
  WildcardRiderIcon
} from "../../assets/images/Icons";

const num = v => Number(v);
const isFinitePair = ([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng);
const toLatLngs = arr =>
  Array.isArray(arr)
    ? arr
        .map(r => [num(r.lat), num(r.lng)])
        .filter(isFinitePair)
    : [];

const SafePolygon = ({ coords, color, keyProp }) => {
  const positions = toLatLngs(coords);
  if (positions.length < 3) return null;
  return (
    <Polygon
      key={keyProp}
      positions={positions}
      pathOptions={{ stroke: false, fillOpacity: 0.2, fillColor: color }}
    />
  );
};

const SafeMarker = ({ lat, lng, icon, k, children }) => {
  const p = [num(lat), num(lng)];
  if (!isFinitePair(p)) return null;
  return (
    <Marker key={k} icon={icon} position={p}>
      {children}
    </Marker>
  );
};

export const DashboardMapHolder = ({
  riders,
  collectionCenters,
  orders,
  isShowOrders,
  isShowRiders,
  isShowBoundary,
  isShowCells,
  isShowBlackListedAreas,
  onCancelOrderClick,
  history,
  mapFull,
  regionDetail,
  isShowCollectionCenters
}) => {
  const center = regionDetail?.centerPoint
    ? [
        num(regionDetail.centerPoint.latitude) || PAKISTAN_LAT_LONG.lat,
        num(regionDetail.centerPoint.longitude) || PAKISTAN_LAT_LONG.lng
      ]
    : null;

  if (!center) return null;

  const getRiderIcon = rider => {
    if (IDLE_RIDER_STATES.includes(+rider.state)) return IdleRiderIcon;
    if (BUSY_RIDER_STATES.includes(+rider.state)) return BusyRiderIcon;
    if (rider.isWildcard) return WildcardRiderIcon;
    if (rider.isCovid) return CovidRiderIcon;
    return BusyRiderIcon;
  };

  return (
    <Map
      center={center}
      zoom={regionDetail?.zoomLevel || 11}
      currentCenter={center}
      currentZoom={regionDetail?.zoomLevel || 11}
      style={{ height: "100%" }}
    >
      {isShowRiders &&
        Array.isArray(riders) &&
        riders.map(rider => (
          <SafeMarker
            k={`rider-${rider.id}`}
            icon={getRiderIcon(rider)}
            lat={rider.lastKnownLatitude}
            lng={rider.lastKnownLongitude}
          >
            <Popup>
              <RiderInfoWindow info={rider} />
            </Popup>
          </SafeMarker>
        ))}

      {isShowCollectionCenters &&
        Array.isArray(collectionCenters) &&
        collectionCenters.map(cc => (
          <SafeMarker
            k={`collection-center-${cc.id}`}
            icon={CollectionCenterIcon}
            lat={cc.latitude}
            lng={cc.longitude}
          >
            <Popup>
              <CollectionCenterInfoWindow info={cc} />
            </Popup>
          </SafeMarker>
        ))}

      {isShowOrders &&
        Array.isArray(orders) &&
        orders.map(order => (
          <SafeMarker
            k={`order-${order.id}`}
            icon={CustomerIcon}
            lat={order.latitude}
            lng={order.longitude}
          >
            <Popup>
              <OrderInfoWindow
                info={order}
                onCancelOrderClick={onCancelOrderClick}
                history={history}
              />
            </Popup>
          </SafeMarker>
        ))}

      {isShowBoundary && (
        <SafePolygon keyProp="boundary" color="#4F5CD3" coords={regionDetail?.polygon} />
      )}

      {isShowBlackListedAreas &&
        Array.isArray(regionDetail?.blackListedAreas) &&
        regionDetail.blackListedAreas.map((area, i) => (
          <SafePolygon keyProp={`bla-${i}`} color="#000000" coords={area?.coordinates} />
        ))}

      {isShowCells &&
        Array.isArray(regionDetail?.cells) &&
        regionDetail.cells.map((cell, i) => (
          <SafePolygon keyProp={`cell-${cell?.id ?? i}`} color="#0000FF" coords={cell?.coordinates} />
        ))}

      {isShowCells &&
        Array.isArray(regionDetail?.cells) &&
        regionDetail.cells.map((cell, i) => (
          <SafeMarker
            k={`cell-centroid-${cell?.id ?? i}`}
            icon={CellIcon}
            lat={cell?.centroid?.lat}
            lng={cell?.centroid?.lng}
          >
            <Popup>
              <CellInfoWindow info={cell} />
            </Popup>
          </SafeMarker>
        ))}
    </Map>
  );
};
